/**
 * Advance recovery, pure (D4 ladder, D22, EC-38, EC-58; TD-4.6, TD-4.19). Decimal.js in, decimal
 * strings out. TWO-REVIEWER AREA: these numbers decide what is taken out of someone's pay.
 *
 *  - `planPayrollRecovery`: what ONE payroll run may recover. The deduction goes on the next
 *    unlocked run of the traveller's CURRENT legal entity, in the same currency only (otherwise
 *    finance recovers by hand); it is capped at `maxPayrollRecoveryPctOfGross` of the monthly gross,
 *    and whatever is over the cap is carried to the next run. What is already scheduled on an
 *    unfinalized run is taken off first, so a retry never schedules twice.
 *  - `projectRecoverySchedule`: the instalments the cap implies, for the schedule view.
 *  - `ledgerOutstanding`: an advance's balance from its recovery ledger, a `reversal` adding back
 *    what the row it reverses took.
 *
 * Nothing here writes a recovery row. D22: the `payroll_deduction` row is written only once the
 * run is FINALIZED (the reconcile job), never when the input is scheduled.
 */

import { Decimal } from 'decimal.js';
import type { ExpenseAdvanceRecoveryKind, RecoveryManualReason } from '../enums.js';
import { ValidationError } from '../errors.js';
import { toMoneyString } from './money.js';

const ZERO = new Decimal(0);
const HUNDRED = new Decimal(100);

/** Default cap, percent of monthly gross (D22). */
export const DEFAULT_MAX_PAYROLL_RECOVERY_PCT = '50';

/** The schedule view stops projecting after this many runs (a tiny salary against a big advance). */
export const MAX_PROJECTED_RECOVERY_RUNS = 60;

function parse(value: string | null | undefined, label: string): Decimal {
  if (value === null || value === undefined) throw new ValidationError(`${label} is missing`);
  let d: Decimal;
  try {
    d = new Decimal(value);
  } catch {
    throw new ValidationError(`${label} is not a number`);
  }
  if (!d.isFinite() || d.lt(0)) throw new ValidationError(`${label} must be zero or more`);
  return d;
}

export interface PayrollRecoveryInput {
  /** The advance's outstanding balance. */
  outstanding: string;
  /** The advance's currency. */
  currency: string;
  /** Already on unfinalized payroll inputs for this advance (0 when none). */
  scheduled: string;
  /** The currency the traveller's CURRENT legal entity pays in; null without an active employment. */
  payrollCurrency: string | null;
  /** The traveller's monthly gross in `payrollCurrency`; null or zero when unknown. */
  monthlyGross: string | null;
  /** Percent, 0–100 (`TravelPolicy.maxPayrollRecoveryPctOfGross`). */
  maxPctOfGross: string;
}

export type PayrollRecoveryPlan =
  | {
      kind: 'deduct';
      currency: string;
      /** What this run recovers. */
      amount: string;
      /** What is left for later runs after this one (and what is already scheduled). */
      carryForward: string;
      /** The per-run cap the amount was held to. */
      cap: string;
    }
  | { kind: 'manual'; reason: RecoveryManualReason }
  | { kind: 'nothing' };

/** The per-run cap: `pct` of `monthlyGross`, rounded DOWN to the paisa so it never exceeds the cap. */
export function payrollRecoveryCap(monthlyGross: string, maxPctOfGross: string): string {
  const pct = parse(maxPctOfGross, 'maxPctOfGross');
  if (pct.gt(HUNDRED)) throw new ValidationError('maxPctOfGross cannot be over 100');
  return toMoneyString(
    parse(monthlyGross, 'monthlyGross').mul(pct).div(HUNDRED).toDecimalPlaces(2, Decimal.ROUND_DOWN),
  );
}

export function planPayrollRecovery(input: PayrollRecoveryInput): PayrollRecoveryPlan {
  const outstanding = parse(input.outstanding, 'outstanding');
  const scheduled = parse(input.scheduled, 'scheduled');
  const due = outstanding.sub(scheduled);
  if (!due.gt(0)) return { kind: 'nothing' };

  if (input.payrollCurrency === null) return { kind: 'manual', reason: 'no_active_employment' };
  if (input.payrollCurrency !== input.currency) return { kind: 'manual', reason: 'currency_mismatch' };
  if (input.monthlyGross === null) return { kind: 'manual', reason: 'no_gross_pay' };

  const cap = new Decimal(payrollRecoveryCap(input.monthlyGross, input.maxPctOfGross));
  if (!cap.gt(0)) return { kind: 'manual', reason: 'no_gross_pay' };

  const amount = Decimal.min(due, cap);
  return {
    kind: 'deduct',
    currency: input.currency,
    amount: toMoneyString(amount),
    carryForward: toMoneyString(due.sub(amount)),
    cap: toMoneyString(cap),
  };
}

/**
 * The run-by-run amounts that recover `outstanding` at `cap` per run: `[cap, cap, …, rest]`.
 * Stops at `maxRuns`; `remainder` is what would still be outstanding after them.
 */
export function projectRecoverySchedule(
  outstanding: string,
  cap: string,
  maxRuns: number = MAX_PROJECTED_RECOVERY_RUNS,
): { instalments: string[]; remainder: string } {
  let left = parse(outstanding, 'outstanding');
  const perRun = parse(cap, 'cap');
  const instalments: string[] = [];
  if (!perRun.gt(0)) return { instalments, remainder: toMoneyString(left) };
  while (left.gt(0) && instalments.length < maxRuns) {
    const take = Decimal.min(left, perRun);
    instalments.push(toMoneyString(take));
    left = left.sub(take);
  }
  return { instalments, remainder: toMoneyString(left) };
}

export interface RecoveryLedgerRow {
  kind: ExpenseAdvanceRecoveryKind;
  /** Always positive: what the row recovered (or, for `reversal`, gave back). */
  amount: string;
}

/**
 * The advance's balance from what was paid out and its recovery rows: every row but a `reversal`
 * takes its amount off, a `reversal` puts its amount back. Never below zero (a ledger that over-
 * recovers is a data error the caller should see, so it throws).
 */
export function ledgerOutstanding(disbursed: string, rows: readonly RecoveryLedgerRow[]): string {
  let balance = parse(disbursed, 'disbursed');
  for (const row of rows) {
    const amount = parse(row.amount, `${row.kind} amount`);
    balance = row.kind === 'reversal' ? balance.add(amount) : balance.sub(amount);
  }
  if (balance.lt(0)) {
    throw new ValidationError('The recovery ledger takes more than the advance paid out');
  }
  return toMoneyString(balance.isZero() ? ZERO : balance);
}
